/* eslint-disable @typescript-eslint/camelcase */
/**
 * Medicion continua de la conexion mientras el panel esta abierto.
 *
 * Igual que la sonda de /network vive en `PanelContainer`: mantiene una llamada
 * al eco, muestrea cada segundo y guarda dos cosas.
 *
 *   - una fila por ventana de 5 minutos (`automatic`), con el resumen;
 *   - los eventos que detecta `EventDetector`, apenas terminan.
 *
 * Si la llamada se cae o la central no responde reintenta con espera creciente,
 * y guarda un evento por caida. Con la pestaña en segundo plano no muestrea
 * (Chrome baja los timers a uno por minuto y falsearia las cifras). Con varias
 * pestañas del panel abiertas mide solo una, la que toma el lock.
 */

import {
    DiagnosticBusyError,
    DiagnosticEventPayload,
    DiagnosticNotConfiguredError,
    fetchDiagnosticConfig,
    finishDiagnosticRun,
    postDiagnosticEvents,
    startDiagnosticRun,
} from '@/api/diagnostic';
import EventDetector, { DetectedEvent } from './events';
import { DiagnosticCancelled, DiagnosticError, runDiagnostic } from './runner';
import { Sample, summarize } from './summary';

export const WINDOW_SECONDS = 300;
// Cada reintento es un REGISTER nuevo contra la central y el firewall puede banear la IP
// por demasiados intentos: la espera crece rapido, tiene jitter y al final queda en 30 min.
const RETRY_MS = [60000, 5 * 60000, 15 * 60000, 30 * 60000];
// sin cupo (la compañia ya tiene 4 midiendo) no se toca la central: solo se vuelve a preguntar
const BUSY_RETRY_MS = 5 * 60000;
const NOT_CONFIGURED_RETRY_MS = 10 * 60 * 1000;
// registros fallidos seguidos tras los que se deja de insistir hasta recargar el panel
const MAX_REGISTER_FAILURES = 6;

// reparte el arranque de todos los operadores para que no registren juntos
const START_JITTER_MS = 20000;
const BACKOFF_KEY = 'panel-diagnostic-backoff';

/**
 * El proximo intento permitido sobrevive a recargar o reabrir el panel: sin esto, F5 o abrir
 * y cerrar la pestaña reinicia la espera y cada recarga es un REGISTER nuevo.
 */
function readNextAt(): number {
    try {
        return Number(window.localStorage.getItem(BACKOFF_KEY)) || 0;
    } catch (error) {
        return 0;
    }
}

function writeNextAt(at: number): void {
    try {
        if (at > 0) {
            window.localStorage.setItem(BACKOFF_KEY, String(at));
        } else {
            window.localStorage.removeItem(BACKOFF_KEY);
        }
    } catch (error) {
        // sin storage el limite queda solo en memoria
    }
}

function withJitter(ms: number): number {
    return Math.round(ms * (0.8 + Math.random() * 0.4));
}
// una medicion que duro menos que esto no cuenta como "estaba andando"
const STABLE_MS = 60000;
const LOCK_NAME = 'panel-diagnostic-measure';

export type MeasureStatus =
    'idle' | 'otherTab' | 'connecting' | 'measuring' | 'paused' | 'retrying' | 'full' | 'unavailable';

/** Lo que muestra la pantalla de diagnostico. */
export interface MeasureState {
    status: MeasureStatus;
    /** Segundos medidos desde que se conecto. */
    seconds: number;
    rtt: number | null;
    lossPct: number;
    jitterMs: number;
    events: number;
    lastEvent: { kind: string; at: number; peak: number | null } | null;
    /** Ultimos `HISTORY_SECONDS` segundos, un valor por segundo, para las graficas. */
    history: { rtt: number[]; loss: number[]; jitter: number[] };
}

export const HISTORY_SECONDS = 300;

const state: MeasureState = {
    status: 'idle',
    seconds: 0,
    rtt: null,
    lossPct: 0,
    jitterMs: 0,
    events: 0,
    lastEvent: null,
    history: { rtt: [], loss: [], jitter: [] },
};

// arreglo nuevo en cada muestra: la vista detecta el cambio por referencia
function pushed(values: number[], value: number): number[] {
    return [...values, value].slice(-HISTORY_SECONDS);
}

// Web Locks no esta en los tipos de TS de este proyecto
const locks = (navigator as any).locks as {
    request: (name: string, options: { signal: AbortSignal }, callback: () => Promise<void>) => Promise<void>;
} | undefined;
let listeners: Array<(value: MeasureState) => void> = [];

function emit(): void {
    listeners.forEach((listener) => listener({ ...state }));
}

function set(changes: Partial<MeasureState>): void {
    Object.assign(state, changes);
    emit();
}

export function onMeasureChange(listener: (value: MeasureState) => void): () => void {
    listeners.push(listener);
    listener({ ...state });

    return () => { listeners = listeners.filter((item) => item !== listener); };
}

/** Una ventana de 5 minutos: la fila que se abrio al empezar y las muestras acumuladas. */
interface Window {
    runId: Promise<number | null>;
    samples: Sample[];
}

let active = false;
let abort: AbortController | null = null;
let wake: (() => void) | null = null;

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
        const timer = window.setTimeout(resolve, ms);
        wake = () => { window.clearTimeout(timer); resolve(); };
    });
}

/** Las muestras son acumuladas: para resumir una ventana se parte de cero en su primera. */
function rebase(samples: Sample[]): Sample[] {
    const [first] = samples;

    return samples.map((sample) => ({
        ...sample,
        received: sample.received - first.received,
        lost: sample.lost - first.lost,
    }));
}

function sendEvents(events: DiagnosticEventPayload[]): void {
    if (!events.length) {
        return;
    }

    const { connection } = navigator as any;
    const withContext = events.map((event) => ({
        ...event,
        effective_type: connection ? connection.effectiveType : undefined,
    }));

    postDiagnosticEvents(withContext).catch((error) => console.warn('No se pudo guardar el evento:', error));
}

function payloadOf(event: DetectedEvent, mediaIp: string | null): DiagnosticEventPayload {
    return {
        kind: event.kind,
        seconds: event.seconds,
        peak: event.peak ?? undefined,
        ago_seconds: event.agoSeconds,
        media_ip: mediaIp || undefined,
    };
}

function closeWindow(win: Window | null, status: 'completed' | 'cancelled'): void {
    if (!win) {
        return;
    }
    if (win.samples.length === 0) {
        // nunca llego a medir: se libera el cupo
        win.runId.then((id) => (id === null ? undefined : finishDiagnosticRun(id, {
            samples: 0, status: 'cancelled', user_agent: navigator.userAgent,
        }))).catch(() => undefined);
        return;
    }

    const measured = rebase(win.samples);
    const summary = summarize(measured);
    // una ventana de menos de 5 segundos fuera del rango del backend: se cierra como cancelada
    const closing = win.samples.length < 5 ? 'cancelled' : status;

    win.runId.then((id) => {
        if (id === null) {
            return undefined;
        }

        return finishDiagnosticRun(id, {
            samples: measured.length,
            status: closing,
            verdict: closing === 'completed' ? summary.verdict : undefined,
            loss_pct: Number(summary.lossPct.toFixed(2)),
            jitter_ms: Math.round(summary.jitterMs),
            rtt_ms: summary.rttMs !== null ? Math.round(summary.rttMs) : undefined,
            cut_seconds: summary.cutSeconds,
            media_ip: summary.mediaIp || undefined,
            user_agent: navigator.userAgent,
        });
    }).catch(() => undefined);
}

/** Una ventana nueva; si ya no hay cupo `onBusy` corta la medicion para no llamar a la central de mas. */
function openWindow(onBusy: () => void): Window {
    return {
        runId: startDiagnosticRun(WINDOW_SECONDS, true).then((run) => run.id).catch((error) => {
            if (error instanceof DiagnosticBusyError) {
                onBusy();
            }

            return null;
        }),
        samples: [],
    };
}

/** Una conexion a la central, hasta que se corte o se pare. Devuelve cuanto estuvo midiendo. */
async function measure(): Promise<number> {
    const config = await fetchDiagnosticConfig();
    // el cupo se pide antes de registrar la extension: sin cupo no se toca la central
    const slot = await startDiagnosticRun(WINDOW_SECONDS, true);
    const detector = new EventDetector();
    let lostSlot = false;
    let win: Window | null = { runId: Promise.resolve(slot.id), samples: [] };
    let previous: Sample | null = null;
    let measuredSeconds = 0;
    let mediaIp: string | null = null;

    const onVisible = () => {
        if (!document.hidden) {
            sendEvents(detector.reset().map((event) => payloadOf(event, mediaIp)));
            previous = null;
            set({ status: 'measuring' });
        } else {
            set({ status: 'paused' });
        }
    };
    document.addEventListener('visibilitychange', onVisible);

    try {
        await runDiagnostic(config, Infinity, {
            onStep: (step) => { set({ status: step === 'measure' ? 'measuring' : 'connecting' }); },
            isCancelled: () => !active || lostSlot,
            isPaused: () => document.hidden,
            onSample: (sample) => {
                mediaIp = sample.mediaIp || mediaIp;
                measuredSeconds += 1;

                const received = previous ? Math.max(0, sample.received - previous.received) : 0;
                const lost = previous ? Math.max(0, sample.lost - previous.lost) : 0;
                previous = sample;
                const rtt = sample.rttMs === null ? state.rtt : Math.round(sample.rttMs);
                const lossPct = received + lost > 0 ? Number(((lost / (received + lost)) * 100).toFixed(1)) : 0;
                const jitterMs = Math.round(sample.jitterMs);
                set({
                    seconds: measuredSeconds,
                    rtt,
                    lossPct,
                    jitterMs,
                    history: {
                        rtt: rtt === null ? state.history.rtt : pushed(state.history.rtt, rtt),
                        loss: pushed(state.history.loss, lossPct),
                        jitter: pushed(state.history.jitter, jitterMs),
                    },
                });

                const events = detector.push(sample);
                if (events.length) {
                    const last = events[events.length - 1];
                    set({
                        events: state.events + events.length,
                        lastEvent: { kind: last.kind, at: Date.now(), peak: last.peak },
                    });
                    sendEvents(events.map((event) => payloadOf(event, mediaIp)));
                }

                if (!win) {
                    win = openWindow(() => { lostSlot = true; });
                }
                win.samples.push(sample);
                if (win.samples.length >= WINDOW_SECONDS) {
                    closeWindow(win, 'completed');
                    win = null;
                }
            },
        });
    } catch (error) {
        sendEvents(detector.flush().map((event) => payloadOf(event, mediaIp)));
        closeWindow(win, error instanceof DiagnosticCancelled ? 'cancelled' : 'completed');

        if (error instanceof DiagnosticCancelled) {
            if (lostSlot) {
                throw new DiagnosticBusyError('domain_busy');
            }

            return measuredSeconds;
        }
        // la llamada se cayo despues de haber andado: es un evento. Si nunca llego a
        // medir lo cuenta quien llama, una sola vez por corte.
        if (error instanceof DiagnosticError && measuredSeconds > 0) {
            sendEvents([{
                kind: 'dropped', seconds: 0, ago_seconds: 0, media_ip: mediaIp || undefined,
            }]);
        }
        throw error;
    } finally {
        document.removeEventListener('visibilitychange', onVisible);
    }

    return measuredSeconds;
}

async function loop(): Promise<void> {
    let attempt = 0;
    let outage = false;
    let registerFailures = 0;

    // antes del primer intento: lo que falte del ultimo castigo mas un desfase al azar
    const firstWait = Math.max(0, readNextAt() - Date.now()) + Math.random() * START_JITTER_MS;
    if (firstWait > 1000) {
        set({ status: firstWait > START_JITTER_MS ? 'retrying' : 'connecting' });
        await sleep(firstWait);
    }

    while (active) {
        const startedAt = Date.now();
        let waitMs = RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)];

        try {
            // eslint-disable-next-line no-await-in-loop
            await measure();
            outage = false;
            registerFailures = 0;
        } catch (error) {
            if (error instanceof DiagnosticNotConfiguredError) {
                set({ status: 'unavailable' });
                waitMs = NOT_CONFIGURED_RETRY_MS;
            } else if (error instanceof DiagnosticBusyError) {
                set({ status: 'full' });
                waitMs = BUSY_RETRY_MS;
            } else {
                // no pudo ni registrarse: un evento por corte, no uno por reintento
                if (error instanceof DiagnosticError && error.kind === 'register' && !outage) {
                    sendEvents([{ kind: 'register_failed', seconds: 0, ago_seconds: 0 }]);
                }
                outage = true;
                registerFailures = error instanceof DiagnosticError && error.kind === 'register'
                    ? registerFailures + 1 : 0;
                if (registerFailures >= MAX_REGISTER_FAILURES) {
                    // la central no deja registrar: se frena, y recargar tampoco adelanta el proximo intento
                    writeNextAt(Date.now() + withJitter(RETRY_MS[RETRY_MS.length - 1]));
                    set({ status: 'unavailable' });
                    break;
                }
                set({ status: 'retrying' });
            }
        }

        if (!active) {
            break;
        }
        attempt = Date.now() - startedAt > STABLE_MS ? 0 : attempt + 1;
        const delay = withJitter(waitMs);
        writeNextAt(Date.now() + delay);
        // eslint-disable-next-line no-await-in-loop
        await sleep(delay);
    }

    if (registerFailures < MAX_REGISTER_FAILURES) {
        set({ status: 'idle' });
    }
}

export function startDiagnosticMeasure(): void {
    if (active) {
        return;
    }

    active = true;
    set({ status: 'connecting', seconds: 0 });

    if (!locks) {
        loop();
        return;
    }

    // con varias pestañas mide una sola; las demas esperan el lock y toman la posta
    // cuando la que mide se cierra
    abort = new AbortController();
    set({ status: 'otherTab' });
    locks
        .request(LOCK_NAME, { signal: abort.signal }, () => {
            set({ status: 'connecting' });
            return loop();
        })
        .catch(() => undefined);
}

export function stopDiagnosticMeasure(): void {
    active = false;
    if (abort) {
        abort.abort();
        abort = null;
    }
    if (wake) {
        wake();
        wake = null;
    }
    set({ status: 'idle' });
}

// En dev el HMR recarga este modulo: la copia vieja seguiria midiendo con el lock y la
// nueva quedaria esperando. Se frena la vieja y la nueva retoma si estaba midiendo.
const { hot } = module as any;
if (hot) {
    if (hot.data && hot.data.wasActive) {
        startDiagnosticMeasure();
    }
    hot.dispose((data: { wasActive: boolean }) => {
        // eslint-disable-next-line no-param-reassign
        data.wasActive = active;
        stopDiagnosticMeasure();
    });
}
